'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';
type ResolvedTheme = Exclude<Theme, 'system'>;

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  systemTheme: ResolvedTheme;
  themes: Theme[];
  setTheme: (theme: Theme) => void;
}

interface ThemeProviderProps {
  children: React.ReactNode;
  initialTheme?: Theme;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children, initialTheme = 'system' }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(initialTheme);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>('light');

  const applyTheme = useCallback((nextTheme: Theme, nextSystemTheme: ResolvedTheme) => {
    const resolvedTheme = nextTheme === 'system' ? nextSystemTheme : nextTheme;
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(resolvedTheme);
    document.documentElement.style.colorScheme = resolvedTheme;
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const storedTheme = readStoredTheme();
    const initialSystemTheme = resolveSystemTheme(mediaQuery.matches);

    // Synchronize React state with the preference applied by the pre-paint bootstrap.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setThemeState(storedTheme);
    setSystemTheme(initialSystemTheme);
    applyTheme(storedTheme, initialSystemTheme);

    const handleSystemThemeChange = (event: MediaQueryListEvent) => {
      const nextSystemTheme = resolveSystemTheme(event.matches);
      setSystemTheme(nextSystemTheme);
      if (readStoredTheme() === 'system') {
        applyTheme('system', nextSystemTheme);
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, [applyTheme]);

  const setTheme = useCallback(
    (nextTheme: Theme) => {
      localStorage.setItem('theme', nextTheme);
      document.cookie = `theme=${nextTheme}; path=/; max-age=31536000; SameSite=Lax`;
      setThemeState(nextTheme);
      applyTheme(nextTheme, systemTheme);
    },
    [applyTheme, systemTheme]
  );

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      resolvedTheme: theme === 'system' ? systemTheme : theme,
      systemTheme,
      themes: ['light', 'dark', 'system'],
      setTheme,
    }),
    [setTheme, systemTheme, theme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

function readStoredTheme(): Theme {
  const storedTheme = localStorage.getItem('theme');
  return storedTheme === 'light' || storedTheme === 'dark' || storedTheme === 'system' ? storedTheme : 'system';
}

function resolveSystemTheme(prefersDark: boolean): ResolvedTheme {
  return prefersDark ? 'dark' : 'light';
}
